import { useEffect, useRef, useState } from "react";
import { API_BASE_URLS } from "../../constants/api";
import { apiUtils } from "../../utils";
import type {
  QuestionnaireAnswerValue,
  QuestionnaireAnswersMap,
  QuestionnaireQuestionOption
} from "../../types";
import { buildAnswersFromSubmission, toArray } from "./questionnaire.utils";
import type {
  Direction,
  FlatQuestionItem,
  QuestionnaireHistoryItem,
  QuestionnaireSectionEntry
} from "./questionnaire.types";

interface UseQuestionnaireFlowProps {
  patientUserId?: string;
  sections: QuestionnaireSectionEntry[];
  currentSectionIndex: number;
  setCurrentSectionIndex: (value: number) => void;
  currentSectionEntry: QuestionnaireSectionEntry | null;
  currentItem: FlatQuestionItem | undefined;
  currentVisibleIndex: number;
  setCurrentVisibleIndex: (value: number) => void;
  visibleQuestions: FlatQuestionItem[];
  answers: QuestionnaireAnswersMap;
  setAnswers: React.Dispatch<React.SetStateAction<QuestionnaireAnswersMap>>;
  answersByQuestionnaireId: Record<string, QuestionnaireAnswersMap>;
  setAnswersByQuestionnaireId: React.Dispatch<
    React.SetStateAction<Record<string, QuestionnaireAnswersMap>>
  >;
  latestSubmissionByQuestionnaireId: Map<string, QuestionnaireHistoryItem>;
  fetchQuestionnaireHistory: () => Promise<QuestionnaireHistoryItem[]>;
  questionnaireVersion: string;
  transitionMs: number;
  onQuestionnaireSubmitted?: () => void | Promise<void>;
}

export function useQuestionnaireFlow({
  patientUserId,
  sections,
  currentSectionIndex,
  setCurrentSectionIndex,
  currentSectionEntry,
  currentItem,
  currentVisibleIndex,
  setCurrentVisibleIndex,
  visibleQuestions,
  answers,
  setAnswers,
  answersByQuestionnaireId,
  setAnswersByQuestionnaireId,
  latestSubmissionByQuestionnaireId,
  fetchQuestionnaireHistory,
  questionnaireVersion,
  transitionMs,
  onQuestionnaireSubmitted
}: UseQuestionnaireFlowProps) {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [sectionSwitchLoading, setSectionSwitchLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [direction, setDirection] = useState<Direction>("next");
  const [outgoingItem, setOutgoingItem] = useState<FlatQuestionItem | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [enterActive, setEnterActive] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [optionTextByQuestionnaireId, setOptionTextByQuestionnaireId] = useState<
    Record<string, Record<string, Record<string, string>>>
  >({});

  const transitionTimeoutRef = useRef<number | null>(null);
  const sectionSwitchTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) window.clearTimeout(transitionTimeoutRef.current);
      if (sectionSwitchTimeoutRef.current) window.clearTimeout(sectionSwitchTimeoutRef.current);
    };
  }, []);

  const clearTransitions = () => {
    if (transitionTimeoutRef.current) {
      window.clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
    setIsTransitioning(false);
    setOutgoingItem(null);
    setEnterActive(false);
  };

  const moveToIndex = (targetIndex: number, moveDirection: Direction) => {
    if (targetIndex === currentVisibleIndex || !currentItem) return;
    clearTransitions();
    setValidationError("");
    setDirection(moveDirection);
    setOutgoingItem(currentItem);
    setIsTransitioning(true);
    setEnterActive(false);
    setCurrentVisibleIndex(targetIndex);

    window.requestAnimationFrame(() => {
      setEnterActive(true);
    });

    transitionTimeoutRef.current = window.setTimeout(() => {
      setIsTransitioning(false);
      setOutgoingItem(null);
      setEnterActive(false);
      transitionTimeoutRef.current = null;
    }, transitionMs);
  };

  const updateAnswer = (questionId: string, value: QuestionnaireAnswerValue) => {
    const questionnaireId = currentSectionEntry?.questionnaireId;
    setAnswers((prev) => {
      const next = { ...prev, [questionId]: value };
      if (questionnaireId) {
        setAnswersByQuestionnaireId((sectionPrev) => ({
          ...sectionPrev,
          [questionnaireId]: next
        }));
      }
      return next;
    });
    setValidationError("");
  };

  const toggleMultiOption = (
    questionId: string,
    currentAnswer: QuestionnaireAnswerValue,
    value: string,
    options: QuestionnaireQuestionOption[] = []
  ) => {
    const selected = toArray(currentAnswer);
    const exists = selected.includes(value);
    const clickedOption = options.find((option) => option.value === value);

    let next: string[];
    if (exists) {
      next = selected.filter((item) => item !== value);
    } else if (clickedOption?.isExclusive) {
      next = [value];
    } else {
      const exclusiveValues = new Set(
        options.filter((option) => option.isExclusive).map((option) => option.value)
      );
      next = [...selected.filter((item) => !exclusiveValues.has(item)), value];
    }

    const questionnaireId = currentSectionEntry?.questionnaireId;
    if (questionnaireId && exists) {
      setOptionTextByQuestionnaireId((prev) => {
        const sectionMap = prev[questionnaireId];
        const questionMap = sectionMap?.[questionId];
        if (!questionMap || !(value in questionMap)) return prev;
        const { [value]: _removed, ...restOptions } = questionMap;
        return {
          ...prev,
          [questionnaireId]: {
            ...sectionMap,
            [questionId]: restOptions
          }
        };
      });
    }

    updateAnswer(questionId, next);
  };

  const clearSingleSelectAnswer = (questionId: string) => {
    const questionnaireId = currentSectionEntry?.questionnaireId;
    if (questionnaireId) {
      setOptionTextByQuestionnaireId((prev) => {
        const sectionMap = prev[questionnaireId];
        if (!sectionMap?.[questionId]) return prev;
        const { [questionId]: _removed, ...restQuestions } = sectionMap;
        return {
          ...prev,
          [questionnaireId]: restQuestions
        };
      });
    }
    updateAnswer(questionId, null);
  };

  const updateOptionText = (questionId: string, optionValue: string, textValue: string) => {
    const questionnaireId = currentSectionEntry?.questionnaireId;
    if (!questionnaireId) return;
    setOptionTextByQuestionnaireId((prev) => ({
      ...prev,
      [questionnaireId]: {
        ...(prev[questionnaireId] || {}),
        [questionId]: {
          ...((prev[questionnaireId] || {})[questionId] || {}),
          [optionValue]: textValue
        }
      }
    }));
  };

  const getOptionText = (questionId: string, optionValue: string) => {
    const questionnaireId = currentSectionEntry?.questionnaireId;
    if (!questionnaireId) return "";
    return optionTextByQuestionnaireId[questionnaireId]?.[questionId]?.[optionValue] || "";
  };

  const startNewQuestionnaire = () => {
    clearTransitions();
    const prefilledByQuestionnaire: Record<string, QuestionnaireAnswersMap> = {};
    sections.forEach(({ questionnaireId }) => {
      prefilledByQuestionnaire[questionnaireId] = buildAnswersFromSubmission(
        latestSubmissionByQuestionnaireId.get(questionnaireId)
      );
    });
    const firstQuestionnaireId = sections[0]?.questionnaireId;
    setAnswers(firstQuestionnaireId ? prefilledByQuestionnaire[firstQuestionnaireId] || {} : {});
    const prefilledOptionTextByQuestionnaire: Record<string, Record<string, Record<string, string>>> = {};
    sections.forEach(({ questionnaireId }) => {
      const latest = latestSubmissionByQuestionnaireId.get(questionnaireId);
      if (!latest) return;
      latest.answers.forEach((entry) => {
        if (!Array.isArray(entry.data)) return;
        entry.data.forEach((item) => {
          if (!item?.value || typeof item.textInput !== "string") return;
          if (!prefilledOptionTextByQuestionnaire[questionnaireId]) {
            prefilledOptionTextByQuestionnaire[questionnaireId] = {};
          }
          if (!prefilledOptionTextByQuestionnaire[questionnaireId][entry.questionId]) {
            prefilledOptionTextByQuestionnaire[questionnaireId][entry.questionId] = {};
          }
          prefilledOptionTextByQuestionnaire[questionnaireId][entry.questionId][item.value] =
            item.textInput;
        });
      });
    });
    setOptionTextByQuestionnaireId(prefilledOptionTextByQuestionnaire);
    setAnswersByQuestionnaireId(prefilledByQuestionnaire);
    setCurrentVisibleIndex(0);
    setValidationError("");
    setSubmitError(null);
    setSectionSwitchLoading(false);
    setCurrentSectionIndex(0);
  };

  const buildSubmissionAnswers = () => {
    const questionMap = new Map(
      (currentSectionEntry?.section.questions || []).map((question) => [question.questionId, question])
    );
    const currentOptionTextMap =
      (currentSectionEntry?.questionnaireId &&
        optionTextByQuestionnaireId[currentSectionEntry.questionnaireId]) ||
      {};
    const payload: Array<{
      questionId: string;
      id: string;
      value?: string;
      data?: Array<{ value: string; textInput?: string }>;
    }> = [];

    Object.entries(answers).forEach(([questionId, value]) => {
      if (value === null || value === undefined || value === "") return;
      if (Array.isArray(value)) {
        if (value.length === 0) return;
        const question = questionMap.get(questionId);
        const optionTextMap = currentOptionTextMap[questionId] || {};
        payload.push({
          questionId,
          id: questionId,
          data: value.map((entry) => {
            const selectedValue = String(entry);
            const selectedOption = question?.options?.find((option) => option.value === selectedValue);
            const textInput = optionTextMap[selectedValue];
            if (selectedOption?.meta?.hasTextInput && typeof textInput === "string" && textInput.trim()) {
              return { value: selectedValue, textInput: textInput.trim() };
            }
            return { value: selectedValue };
          })
        });
        return;
      }
      payload.push({
        questionId,
        id: questionId,
        value: String(value)
      });
    });

    return payload;
  };

  const submitQuestionnaire = async (onDoneLastSection: () => void) => {
    if (!patientUserId) return;
    if (!currentSectionEntry?.questionnaireId) {
      setSubmitError("Questionnaire section is not loaded.");
      return;
    }
    const payloadAnswers = buildSubmissionAnswers();
    if (payloadAnswers.length === 0) {
      setSubmitError("Please answer at least one question before submitting.");
      return;
    }

    setSubmitLoading(true);
    setSubmitError(null);
    try {
      const requestOptions = apiUtils.createRequestOptions("POST", {
        questionnaireId: currentSectionEntry.questionnaireId,
        questionnaireVersion,
        answers: payloadAnswers
      });
      const response = await fetch(
        `${API_BASE_URLS.HELIUS_SERVICE}/patients/${patientUserId}/questionnaires`,
        requestOptions
      );
      await apiUtils.handleResponse(response);
      const isLastSection = currentSectionIndex >= sections.length - 1;
      if (!isLastSection) {
        setSectionSwitchLoading(true);
      }
      clearTransitions();
      const submittedQuestionnaireId = currentSectionEntry.questionnaireId;
      setCurrentVisibleIndex(0);
      setValidationError("");
      const refreshedSubmissions = await fetchQuestionnaireHistory();
      const latestForSection = refreshedSubmissions.find(
        (entry) => entry.questionnaireId === submittedQuestionnaireId
      );
      if (latestForSection) {
        const latestAnswers = buildAnswersFromSubmission(latestForSection);
        setAnswersByQuestionnaireId((prev) => ({
          ...prev,
          [submittedQuestionnaireId]: latestAnswers
        }));
      }
      void Promise.resolve(onQuestionnaireSubmitted?.()).catch(() => undefined);
      if (isLastSection) {
        setSectionSwitchLoading(false);
        onDoneLastSection();
      } else {
        const nextSectionIndex = Math.min(currentSectionIndex + 1, sections.length - 1);
        const nextQuestionnaireId = sections[nextSectionIndex]?.questionnaireId;
        setCurrentSectionIndex(nextSectionIndex);
        setAnswers((nextQuestionnaireId && answersByQuestionnaireId[nextQuestionnaireId]) || {});
        if (sectionSwitchTimeoutRef.current) {
          window.clearTimeout(sectionSwitchTimeoutRef.current);
        }
        sectionSwitchTimeoutRef.current = window.setTimeout(() => {
          setSectionSwitchLoading(false);
          sectionSwitchTimeoutRef.current = null;
        }, 220);
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Failed to submit questionnaire.");
      setSectionSwitchLoading(false);
    } finally {
      setSubmitLoading(false);
    }
  };

  const goNext = (onDoneLastSection: () => void) => {
    if (currentVisibleIndex < visibleQuestions.length - 1) {
      moveToIndex(currentVisibleIndex + 1, "next");
      return;
    }
    void submitQuestionnaire(onDoneLastSection);
  };

  const goBack = () => {
    if (currentVisibleIndex > 0) {
      moveToIndex(currentVisibleIndex - 1, "back");
    }
  };

  return {
    submitLoading,
    sectionSwitchLoading,
    submitError,
    direction,
    outgoingItem,
    isTransitioning,
    enterActive,
    validationError,
    setValidationError,
    setSubmitError,
    setSectionSwitchLoading,
    clearTransitions,
    updateAnswer,
    clearSingleSelectAnswer,
    toggleMultiOption,
    startNewQuestionnaire,
    updateOptionText,
    getOptionText,
    goNext,
    goBack
  };
}
